import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AppPrefillState,
  AvatarMaterialDto,
  DeliverableDto,
  DeliverablesResponse,
  HandoverAutomationDto,
  Locale,
  MilestoneDto,
  MilestonesResponse,
  ResourceDto,
  ResourcesResponse,
  SalespageDeliveryDto,
  SalesStepDto,
  SectionDto,
  PartnerDto
} from "../../shared/dto";
import type { ClientSimulationSnapshot, RailStep, RailTarget } from "../rail-model";
import { DeliverySectionContent } from "./DeliveryRouter";
import { simulatedStripeConnectPath } from "./StripeSection";
import type { ClientAction, M5bSurface } from "./shared";
import {
  actionIssueFromResponse,
  actionIssueFromThrown,
  readErrorCode,
  type ActionIssue
} from "../action-error";
import { READONLY_BLOCKED_ERROR, reportBlockedWrite } from "../readonly";

/**
 * N03: the refusal belongs to the surface that asked for it AND carries its
 * reason. Six delivery sections read this one value, so the server's named
 * code reaches all of them from a single place.
 */
export function isSurfaceActionError(
  errorSurface: { surface: M5bSurface; issue: ActionIssue } | null,
  currentSurface: M5bSurface
): ActionIssue | null {
  return errorSurface && errorSurface.surface === currentSurface ? errorSurface.issue : null;
}

export function mergeWalkthroughStripeResources(
  snapshotResources: ResourceDto[],
  serverResources: ResourceDto[],
  currentOrigin: string
): ResourceDto[] {
  const serverStripe = serverResources.find((item) => item.key === "stripe_setup");
  const rawConnectUrl = serverStripe?.value.connect_url;
  const safePath = typeof rawConnectUrl === "string"
    ? simulatedStripeConnectPath(rawConnectUrl, currentOrigin)
    : null;
  if (!serverStripe || !safePath) return snapshotResources;

  const nextStripe: ResourceDto = {
    ...(snapshotResources.find((item) => item.key === "stripe_setup") ?? serverStripe),
    key: "stripe_setup",
    value: {
      ...(snapshotResources.find((item) => item.key === "stripe_setup")?.value ?? {}),
      client_invited_team: serverStripe.value.client_invited_team === true,
      connect_url: safePath
    },
    updatedAt: serverStripe.updatedAt
  };
  const replaced = snapshotResources.some((item) => item.key === "stripe_setup");
  return replaced
    ? snapshotResources.map((item) => item.key === "stripe_setup" ? nextStripe : item)
    : [...snapshotResources, nextStripe];
}

export function M5bSectionView(props: {
  surface: M5bSurface;
  sections: SectionDto[];
  csrfToken: string;
  locale: Locale;
  includesApp: boolean;
  /** F143: THIS client's journey; the sales overview numbers its rows from it. */
  railSteps: ReadonlyArray<RailStep>;
  membershipRole: "owner" | "collaborator";
  partner: PartnerDto | null;
  profilePhotoUrl: string | null;
  t: (key: string) => string;
  onChanged: () => Promise<void>;
  onNavigate?: (target: RailTarget) => void;
  testSnapshot?: ClientSimulationSnapshot;
  testMode?: boolean;
}) {
  const [deliverables, setDeliverables] = useState<DeliverableDto[]>(props.testSnapshot?.delivery.deliverables ?? []);
  const [avatarMaterial, setAvatarMaterial] = useState<AvatarMaterialDto | null>(props.testSnapshot?.delivery.avatar ?? null);
  const [salespageDelivery, setSalespageDelivery] = useState<SalespageDeliveryDto | null>(props.testSnapshot?.delivery.salespage ?? null);
  const [salesSteps, setSalesSteps] = useState<SalesStepDto[]>(props.testSnapshot?.delivery.salesSteps ?? []);
  const [resources, setResources] = useState<ResourceDto[]>(props.testSnapshot?.resources ?? []);
  const [milestones, setMilestones] = useState<MilestoneDto[]>(props.testSnapshot?.milestones ?? []);
  const [handoverAutomation, setHandoverAutomation] = useState<HandoverAutomationDto | null>(null);
  // F135b: absent until the server says a machine prefill is really running.
  const [appPrefill, setAppPrefill] = useState<AppPrefillState | null>(null);
  const [dataErrors, setDataErrors] = useState<Set<string>>(new Set());
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [actionErrorSurface, setActionErrorSurface] = useState<{ surface: M5bSurface; issue: ActionIssue } | null>(null);
  const [loading, setLoading] = useState(!props.testSnapshot);
  const [feedbackRevision, setFeedbackRevision] = useState(0);
  const surfaceRef = useRef(props.surface);

  const loadData = useCallback(async () => {
    if (props.testSnapshot) {
      setDeliverables(props.testSnapshot.delivery.deliverables);
      setAvatarMaterial(props.testSnapshot.delivery.avatar ?? null);
      setSalespageDelivery(props.testSnapshot.delivery.salespage ?? null);
      setSalesSteps(props.testSnapshot.delivery.salesSteps ?? []);
      let walkthroughResources = props.testSnapshot.resources;
      if (props.testMode) {
        try {
          const response = await fetch("/api/resources?test=1", { credentials: "include" });
          const body = await response.json() as ResourcesResponse;
          if (!response.ok || !body.ok) throw new Error("resources_load_failed");
          walkthroughResources = mergeWalkthroughStripeResources(
            walkthroughResources,
            body.resources,
            window.location.origin
          );
        } catch {
          // The synthetic snapshot remains truthful: without the authenticated
          // dev response it shows the preparing state and no connect action.
        }
      }
      setResources(walkthroughResources);
      setMilestones(props.testSnapshot.milestones);
      setLoading(false);
      return;
    }
    const requests = [
      ["deliverables", "/api/deliverables"],
      ["resources", "/api/resources"],
      ["milestones", "/api/milestones"]
    ] as const;
    const failed = new Set<string>();
    await Promise.all(requests.map(async ([key, path]) => {
      try {
        const response = await fetch(path, { credentials: "include" });
        const body = await response.json() as DeliverablesResponse | ResourcesResponse | MilestonesResponse;
        if (!response.ok || !body.ok) throw new Error(`${key}_load_failed`);
        if (key === "deliverables") {
          setDeliverables((body as DeliverablesResponse).deliverables);
          setAvatarMaterial((body as DeliverablesResponse).avatar ?? null);
          setSalespageDelivery((body as DeliverablesResponse).salespage ?? null);
          setSalesSteps((body as DeliverablesResponse).salesSteps ?? []);
        }
        if (key === "resources") {
          setResources((body as ResourcesResponse).resources);
          setHandoverAutomation((body as ResourcesResponse).handoverAutomation ?? null);
          setAppPrefill((body as ResourcesResponse).appPrefill ?? null);
        }
        if (key === "milestones") setMilestones((body as MilestonesResponse).milestones);
      } catch {
        failed.add(key);
      }
    }));
    setDataErrors(failed);
    setLoading(false);
  }, [props.testMode, props.testSnapshot]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    surfaceRef.current = props.surface;
    setActionErrorSurface(null);
  }, [props.surface]);

  useEffect(() => {
    const refreshServerTruth = () => void loadData();
    window.addEventListener("focus", refreshServerTruth);
    return () => window.removeEventListener("focus", refreshServerTruth);
  }, [loadData]);

  async function runAction(action: ClientAction, payload: Record<string, unknown> = {}) {
    const actionSurface = props.surface;
    const paths: Record<ClientAction, string> = {
      "bot-accept": "/api/delivery/bot-accepted",
      "apple-start": "/api/app/apple-account-started",
      "app-interest": "/api/app/interest"
    };
    setBusyAction(action);
    setActionErrorSurface(null);
    // The refused answer is read HERE, not thrown away: a `throw` inside the
    // try would arrive in the catch as a string nobody can map back (N03).
    const refuse = (issue: ActionIssue) => {
      setActionErrorSurface(surfaceRef.current === actionSurface ? { surface: actionSurface, issue } : null);
    };
    try {
      const response = await fetch(paths[action], {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": crypto.randomUUID()
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const code = await readErrorCode(response);
        // Act-approve (2026-08-19): the impersonation gate's refusal has ONE
        // home, the bar, which names it. The generic `forbidden` sentence here
        // told Pavel "napiš parťákovi" while HE was the partner.
        if (code === READONLY_BLOCKED_ERROR) {
          reportBlockedWrite("POST", paths[action]);
          return;
        }
        refuse(actionIssueFromResponse(response.status, code));
        return;
      }
      const body = await response.json().catch(() => null) as { ok?: boolean; error?: string } | null;
      if (body?.ok !== true) {
        refuse(actionIssueFromResponse(200, body?.error));
        return;
      }
      await Promise.all([loadData(), props.onChanged()]);
    } catch {
      refuse(actionIssueFromThrown());
    } finally {
      setBusyAction(null);
    }
  }

  // Gate-document actions carry the deliverable id in the path (approve /
  // feedback on the audit or plan). Returns success so composers can reset.
  async function runGateAction(key: string, path: string, payload: Record<string, unknown>): Promise<boolean> {
    const actionSurface = props.surface;
    setBusyAction(key);
    setActionErrorSurface(null);
    const refuse = (issue: ActionIssue) => {
      setActionErrorSurface(surfaceRef.current === actionSurface ? { surface: actionSurface, issue } : null);
      return false;
    };
    try {
      const response = await fetch(path, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": crypto.randomUUID()
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const code = await readErrorCode(response);
        // Act-approve (2026-08-19): same as runAction - the gate's refusal is
        // the bar's to name, never this surface's generic sentence. This is
        // the exact path Pavel hit: the strategy approve under impersonation.
        if (code === READONLY_BLOCKED_ERROR) {
          reportBlockedWrite("POST", path);
          return false;
        }
        return refuse(actionIssueFromResponse(response.status, code));
      }
      const body = await response.json().catch(() => null) as { ok?: boolean; error?: string } | null;
      if (body?.ok !== true) return refuse(actionIssueFromResponse(200, body?.error));
      if (key.startsWith("gate-feedback:")) setFeedbackRevision((value) => value + 1);
      await Promise.all([loadData(), props.onChanged()]);
      return true;
    } catch {
      return refuse(actionIssueFromThrown());
    } finally {
      setBusyAction(null);
    }
  }

  // First load shows a pulse (consistent with M4/ProfileView); later reloads
  // keep the current content in place so actions do not blank the screen.
  if (loading && !props.testSnapshot) {
    return <section className="m5b-view m4-state" aria-live="polite"><div className="loading-pulse" /><p>{props.t("common.loading")}</p></section>;
  }

  const viewDeliverables = props.testSnapshot?.delivery.deliverables ?? deliverables;
  const viewSalespageDelivery = props.testSnapshot?.delivery.salespage ?? salespageDelivery;
  const viewSalesSteps = props.testSnapshot?.delivery.salesSteps ?? salesSteps;
  const viewResources = resources;
  const viewMilestones = props.testSnapshot?.milestones ?? milestones;
  return (
    <DeliverySectionContent
      {...props}
      deliverables={viewDeliverables}
      avatarMaterial={avatarMaterial}
      salespageDelivery={viewSalespageDelivery}
      salesSteps={viewSalesSteps}
      onReloadData={loadData}
      resources={viewResources}
      milestones={viewMilestones}
      handoverAutomation={handoverAutomation}
      appPrefill={appPrefill}
      dataErrors={dataErrors}
      busyAction={busyAction}
      actionError={isSurfaceActionError(actionErrorSurface, props.surface)}
      feedbackRevision={feedbackRevision}
      onAction={runAction}
      onGateApprove={(deliverableId) => runGateAction(`gate-approve:${deliverableId}`, `/api/deliverables/${deliverableId}/approve`, {})}
      onGateFeedback={(deliverableId, payload) => runGateAction(`gate-feedback:${deliverableId}`, `/api/deliverables/${deliverableId}/feedback`, payload)}
    />
  );
}
